import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useGetAdminMenusQuery } from '@/store/api/DownlineApi';
import { setAdminMenus } from '@/lib/adminMenus';
import { isAdminRouteHidden } from '@/lib/adminMenuRoutes';

/**
 * Per-brand panel access, enforced at the router.
 *
 * The sidebar drops the rows the super admin switched off; this closes the URLs
 * behind them, so a typed address, an old bookmark and a shared link all land
 * where the button used to be instead of rendering the page anyway.
 *
 * Navigation, not authorization: the APIs behind a hidden page still answer.
 */
export function AdminMenuGate() {
  const { pathname } = useLocation();

  // The gate fetches its OWN map instead of reading one the sidebar loads.
  //
  // The sidebar lives inside `DashboardLayout`, which is this gate's child, so
  // the gate used to judge a URL with a map only the page it was judging could
  // supply. First render therefore always ran on whatever the module store
  // happened to hold — nothing on a cold start, and the PREVIOUS account's rail
  // after a sign-out, which is what flashed the refusal page on login. Worse, a
  // refusal unmounted the sidebar, and with `keepUnusedDataFor: 0` the cached
  // map went with it, leaving nothing to fetch a corrected one.
  //
  // Both still call the same endpoint, so RTK Query serves one request to both,
  // and the gate's subscription keeps the entry alive while a page is refused.
  const { data, isLoading, isUninitialized } = useGetAdminMenusQuery();

  // Mirror it for `isAdminRouteHidden`'s default argument and anything else
  // reading from outside the tree.
  useEffect(() => {
    setAdminMenus(data?.menus);
  }, [data]);

  // Signing out unmounts the gate. Drop the map on the way, so the next account
  // is never judged by the last one's rail.
  useEffect(() => () => setAdminMenus(undefined), []);

  // Judge nothing until this session's own map has answered. A verdict reached
  // before it lands is a guess, and a wrong guess here is a full-page refusal.
  if (isUninitialized || isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-32 w-32 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  // A failed lookup leaves this empty, which every check reads as "allowed" —
  // nobody is locked out of their own panel because one request did not answer.
  if (!isAdminRouteHidden(pathname, data?.menus ?? {})) return <Outlet />;

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-2 text-center">
      <p className="text-lg font-semibold text-gray">This page is not available</p>
      <p className="max-w-sm text-sm text-gray/60">
        Your administrator has turned this section off for your account.
      </p>
    </div>
  );
}
